"use client";

import { useEffect, useState } from "react";
import { reportClientException } from "@/lib/analytics/report-client-exception";

interface SiteErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

const isChunkLoadError = (error: Error): boolean => {
  // Next.js wraps these as `ChunkLoadError`. Webpack's runtime throws plain
  // Errors whose message includes "Loading chunk". Cover both shapes.
  return (
    error.name === "ChunkLoadError" ||
    /Loading chunk \d+ failed/i.test(error.message) ||
    /Failed to load chunk/i.test(error.message) ||
    /ChunkLoadError/.test(error.message)
  );
};

// One-shot guard so we don't trap a visitor in a hard-refresh loop when the
// chunk failure is persistent (offline, blocked CDN, ad-blocker stripping
// /_next URLs, …). After a single auto-reload attempt we render the manual
// fallback UI so the user can choose to reload or navigate away.
const CHUNK_RELOAD_GUARD_KEY = "ai-primer:chunk-reload-attempted";

const consumeChunkReloadAttempt = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    if (window.sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY) === "1") {
      return true;
    }
    window.sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, "1");
    return false;
  } catch {
    // sessionStorage can throw in private mode / Safari ITP. If we can't
    // remember the attempt, fall back to the safer side: skip auto-reload
    // entirely so we never loop.
    return true;
  }
};

/**
 * Route-level error boundary for the (site) segment. Renders when a server
 * component throws or a client component throws during render.
 *
 * Two responsibilities:
 * 1. Report the failure to PostHog with a stable `error_boundary` source so
 *    we can tell route-level renders from API-route or runtime errors.
 * 2. Self-heal `ChunkLoadError` by triggering a full reload — this happens
 *    when a visitor stayed on a tab across a deploy and Next.js can't fetch
 *    the now-stale JS chunk. Without the reload they're stuck on a blank
 *    fallback.
 */
export default function SiteError({ error, reset }: SiteErrorProps) {
  const [isReloading, setIsReloading] = useState(false);

  useEffect(() => {
    const chunkLoadFailure = isChunkLoadError(error);
    reportClientException({
      error,
      source: "site_error_boundary",
      extra: {
        digest: error.digest,
        is_chunk_load_error: chunkLoadFailure,
      },
    });

    if (chunkLoadFailure && typeof window !== "undefined") {
      // Only auto-reload once per session. A persistent chunk failure (no
      // network, blocked CDN, ad-blocker rewriting /_next URLs) would
      // otherwise trap the visitor in an endless refresh loop with no
      // access to the manual fallback UI.
      if (consumeChunkReloadAttempt()) {
        return;
      }
      setIsReloading(true);
      // Schedule a hard reload — the user is sitting on stale JS that no
      // longer exists on the server. A `reset()` would re-run the same
      // failing import, so a full reload is the correct fix here.
      window.location.reload();
    }
  }, [error]);

  if (isReloading) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <p className="text-gray-600">Updating to the latest version…</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12 text-center">
      <h1 className="mb-4 font-bold text-2xl">Something went wrong</h1>
      <p className="mb-6 text-gray-600">
        We hit an unexpected error rendering this page. The team has been
        notified.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <button
          className="rounded-full bg-[var(--explore-accent-yellow,#facc15)] px-5 py-2 font-semibold text-[var(--explore-on-accent-text,#000)]"
          onClick={() => reset()}
          type="button"
        >
          Try again
        </button>
        <a
          className="rounded-full border border-gray-300 px-5 py-2 font-semibold text-gray-700"
          href="/"
        >
          Go to homepage
        </a>
      </div>
      {error.digest && (
        <p className="mt-6 text-gray-400 text-xs">
          Reference: <code>{error.digest}</code>
        </p>
      )}
    </div>
  );
}
